import { escapeHtml } from './html-entities';
import { BULLET_MARKER } from './rich-text-to-plain-text';

const bulletRegex = new RegExp(`^${BULLET_MARKER}\\s+`);
const orderedRegex = /^\d+\.\s+/;

// A marker only opens and closes against a non-space, so arithmetic ("2 * 3 * 4") and the
// stray asterisks providers use as separators stay literal. Longest marker first, so `***`
// is not eaten as a bold plus a stray asterisk.
//
// The inner group is lazy (`??`) rather than optional-greedy: a greedy `(?:…)?` prefers a
// long match and only falls back to empty once every longer one fails, which made a second
// run on the same line get swallowed — `*a* and *b*` closed on the LAST asterisk and
// produced `<i>a* and *b</i>`.
const boldItalicRegex = /\*\*\*([^\s*](?:.*?[^\s*])??)\*\*\*/g;
const boldRegex = /\*\*([^\s*](?:.*?[^\s*])??)\*\*/g;
const italicRegex = /\*([^\s*](?:.*?[^\s*])??)\*/g;

// Escaping happens first, so the only markup in the line from here on is ours.
const toInlineHtml = (line: string) =>
  escapeHtml(line)
    .replace(boldItalicRegex, '<b><i>$1</i></b>')
    .replace(boldRegex, '<b>$1</b>')
    .replace(italicRegex, '<i>$1</i>');

const toListItems = (lines: string[], strip: RegExp) =>
  lines
    .map((line) => `<li>${toInlineHtml(line.replace(strip, ''))}</li>`)
    .join('');

// A block is one paragraph or one list — whatever sat between blank lines in the editor.
const toBlockHtml = (block: string) => {
  const lines = block.split('\n');

  if (lines.every((line) => bulletRegex.test(line))) {
    return `<ul>${toListItems(lines, bulletRegex)}</ul>`;
  }

  if (lines.every((line) => orderedRegex.test(line))) {
    return `<ol>${toListItems(lines, orderedRegex)}</ol>`;
  }

  return `<p>${lines.map(toInlineHtml).join('<br>')}</p>`;
};

// The inverse of `htmlToPlainText` for everything a plain textarea can express: paragraphs,
// lists, and `**bold**` / `*italic*`. Underline is the one mark with no plain-text spelling.
export const plainTextToHtml = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }

  return trimmed
    .replace(/\r\n?/g, '\n')
    .split(/\n{2,}/)
    .map(toBlockHtml)
    .join('');
};
